import React from "react";
import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Box,
  Divider,
  Typography,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import { FeedbackQuestionEvaluationDto, ReferentsEvaluationDto } from "../../../types/workshopTypes";

interface WorkshopInstanceEvaluationTableProps {
  feedbackQuestionsEvaluation: FeedbackQuestionEvaluationDto[];
  referentsEvaluation: ReferentsEvaluationDto[];
  averageGrade: number;
}

/**
 * This component is responsible for rendering the table for the workshop instance evaluation page.
 * @param feedbackQuestionsEvaluation the questions of the workshop instance feedback
 * @param referentsEvaluation the referents of the workshop instance feedback
 * @param averageGrade the average grade of the workshop instance feedback
 * @returns the table for the workshop instance evaluation page
 */
const WorkshopInstanceEvaluationTable: React.FunctionComponent<WorkshopInstanceEvaluationTableProps> = ({
  feedbackQuestionsEvaluation,
  referentsEvaluation,
  averageGrade,
}) => {
  // Get unique workshop feedback ids from both questions and referents to display all feedbacks in the table
  const uniqueWorkshopFeedbackIds = Array.from(
    new Set(feedbackQuestionsEvaluation.flatMap((question) => question.feedbacks.map((b) => b.workshopFeedbackId)))
  );

  // Calculate the average grade for each feedback id to display in the last column of the table
  const averages = uniqueWorkshopFeedbackIds.map((id) => {
    const notes = [
      ...feedbackQuestionsEvaluation.map((question) => {
        const feedback = question.feedbacks.find((b) => b.workshopFeedbackId === id);
        return feedback && feedback.grade !== null ? feedback.grade : 0;
      }),
      ...referentsEvaluation.map((ref) => {
        const feedback = ref.feedbacks.find((b) => b.workshopFeedbackId === id);
        return feedback && feedback.grade !== null ? feedback.grade : 0;
      }),
    ].filter((grade) => grade !== 0 && grade !== undefined);

    const sum = notes.reduce((a, b) => a + b, 0);
    const avg = sum / (notes.length || 1);

    return avg.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });

  return feedbackQuestionsEvaluation.length > 0 ? (
    <Box sx={{ pb: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography fontWeight={"bold"} fontSize={18}>
        Übersicht:
      </Typography>
      {referentsEvaluation.length > 0
        ? referentsEvaluation.map((ref, index) => {
            return (
              <Stack direction={"row"} key={ref.memberId}>
                <Typography fontSize={14} fontWeight={"bold"}>
                  Referent {index + 1}: &nbsp;
                </Typography>
                <Link to={`/gesamtuebersicht/${ref.memberId}`} style={{ textDecoration: "none", color: "inherit" }}>
                  <Typography fontSize={14}>
                    {ref.firstName} {ref.lastName}
                  </Typography>
                </Link>
              </Stack>
            );
          })
        : null}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                style={{ verticalAlign: "bottom", fontWeight: "bold", padding: 0, paddingBottom: 5 }}
                align="center"
              >
                <Box>Nr.</Box>
              </TableCell>
              {feedbackQuestionsEvaluation.map((question) => (
                <TableCell
                  key={question.feedbackQuestionId}
                  style={{ verticalAlign: "bottom", fontWeight: "bold" }}
                  align="center"
                >
                  F{question.feedbackQuestionId}
                </TableCell>
              ))}
              {referentsEvaluation.map((ref, index) => (
                <TableCell key={ref.memberId} style={{ verticalAlign: "bottom", fontWeight: "bold" }} align="center">
                  R{index + 1}
                </TableCell>
              ))}
              <TableCell style={{ verticalAlign: "bottom", fontWeight: "bold", maxWidth: 80 }}>Durchschnitt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uniqueWorkshopFeedbackIds.map((id, index) => (
              <TableRow key={id}>
                <TableCell sx={{ p: 0, fontWeight: "bold" }}> {index + 1}</TableCell>
                {feedbackQuestionsEvaluation.map((question) => {
                  const feedback = question.feedbacks.find((b) => b.workshopFeedbackId === id);
                  return (
                    <TableCell
                      key={question.feedbackQuestionId}
                      sx={{ paddingLeft: 1, borderLeft: 1, borderLeftColor: "#e4e4e4" }}
                      align="center"
                    >
                      {feedback?.grade === null ? "-" : feedback?.grade.toLocaleString("de-DE")}
                    </TableCell>
                  );
                })}
                {referentsEvaluation.map((ref) => {
                  const feedback = ref.feedbacks.find((b) => b.workshopFeedbackId === id);
                  return (
                    <TableCell key={ref.memberId} sx={{ borderLeft: 1, borderLeftColor: "#e4e4e4" }} align="center">
                      {feedback?.grade === null ? "-" : feedback?.grade.toLocaleString("de-DE")}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ borderLeft: 1, borderLeftColor: "#e4e4e4" }}>{averages[index]}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ p: 0, borderTopColor: "black", borderTop: 2, borderBottom: 0 }}>
              <TableCell sx={{ p: 0, pr: 1, color: "primary.main", fontWeight: "bold", borderBottom: 0 }} align="left">
                Ø
              </TableCell>
              {feedbackQuestionsEvaluation.map((question) => (
                <TableCell sx={{ p: 0, fontWeight: "bold", borderBottom: 0 }} align="center">
                  {question.average.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              ))}
              {referentsEvaluation.map((ref) => (
                <TableCell sx={{ p: 0, fontWeight: "bold", borderBottom: 0 }} align="center">
                  {ref.average.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: "bold", maxWidth: 80, borderBottom: 0 }}>
                {averageGrade.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ) : null;
};

export default WorkshopInstanceEvaluationTable;
